import { PublicPageShell } from '@/components/PublicPageShell';
import type { Lang } from './LandingPage';

interface PrivacyPageProps {
  lang: Lang;
  setLang: (lang: Lang) => void;
  onBack: () => void;
}

const content = {
  zh: {
    title: '隐私政策',
    intro: 'TingYue 重视你的隐私。本政策说明我们如何收集、使用和保护你的信息。',
    sections: [
      {
        title: '1. 我们收集哪些信息',
        text: '为了提供量化交易服务，我们收集你注册时提供的邮箱地址、你录入的交易记录、自选股、组合配置以及你自愿填写的大模型 API Key。',
      },
      {
        title: '2. 信息如何存储',
        text: '在默认自托管模式下，所有数据均保存在你自己的服务器或设备上的 SQLite 数据库中。我们不会将数据上传到 TingYue 官方服务器。',
      },
      {
        title: '3. API Key 安全',
        text: '你配置的大模型 API Key 会在服务端使用 AES 加密存储，密钥由你在 .env 文件中设置的 LLM_KEY_ENCRYPTION_KEY 控制。TingYue 不会读取、记录或转售你的 API Key。',
      },
      {
        title: '4. Cookie 与本地存储',
        text: '我们使用浏览器 localStorage 存储登录态（JWT Token）和主题偏好，以便你下次访问时保持登录状态。这些信息不包含密码。',
      },
      {
        title: '5. 第三方数据',
        text: '行情数据来自公开网络接口，AI 分析由你选择的大模型服务商处理。请同时阅读对应服务商的隐私政策。',
      },
      {
        title: '6. 数据控制权',
        text: '你可以随时导出或删除自己的交易记录、组合和账户数据。删除账户后，相关数据将从你的服务器中移除。',
      },
      {
        title: '7. 政策更新',
        text: '我们可能会根据功能变化更新本隐私政策。更新后的政策将在网站上发布。',
      },
    ],
    contact: '如有隐私相关问题，请通过 GitHub Issues 联系我们。',
  },
  en: {
    title: 'Privacy Policy',
    intro: 'TingYue respects your privacy. This policy explains how we collect, use and protect your information.',
    sections: [
      {
        title: '1. Information We Collect',
        text: 'To provide quantitative trading services, we collect your email address at registration, trade records you enter, watchlists, portfolio configurations, and LLM API keys you voluntarily provide.',
      },
      {
        title: '2. How Information Is Stored',
        text: 'Under the default self-hosted mode, all data is stored in a SQLite database on your own server or device. We do not upload data to TingYue official servers.',
      },
      {
        title: '3. API Key Security',
        text: 'LLM API keys you configure are encrypted at rest on the server using AES. The encryption key is controlled by the LLM_KEY_ENCRYPTION_KEY you set in the .env file. TingYue does not read, log or resell your API keys.',
      },
      {
        title: '4. Cookies and Local Storage',
        text: 'We use browser localStorage to store login state (JWT token) and theme preference so you remain logged in on your next visit. This information does not contain your password.',
      },
      {
        title: '5. Third-Party Data',
        text: 'Market data comes from public network interfaces, and AI analysis is processed by the LLM provider you choose. Please also review the privacy policies of the corresponding service providers.',
      },
      {
        title: '6. Data Control',
        text: 'You can export or delete your trade records, portfolios and account data at any time. After account deletion, related data is removed from your server.',
      },
      {
        title: '7. Policy Updates',
        text: 'We may update this privacy policy as features change. The updated policy will be posted on the website.',
      },
    ],
    contact: 'If you have any privacy-related questions, please contact us via GitHub Issues.',
  },
};

export function PrivacyPage({ lang, setLang, onBack }: PrivacyPageProps) {
  const c = content[lang];
  return (
    <PublicPageShell title={c.title} lang={lang} setLang={setLang} onBack={onBack}>
      <p className="text-muted-foreground mb-8">{c.intro}</p>
      {c.sections.map((s, i) => (
        <div key={i} className="mb-6">
          <h2 className="text-xl font-semibold mb-2">{s.title}</h2>
          <p className="text-muted-foreground leading-relaxed">{s.text}</p>
        </div>
      ))}
      <p className="text-sm text-muted-foreground mt-8">{c.contact}</p>
    </PublicPageShell>
  );
}
